// =====================================================================
// src/modulos/governanca.js — Tarefas do Dia (camareira), a ficha, e a
// Supervisão. O desenho é o do app do Monteiro (Tarefas do Dia com os
// cartões nas quatro cores; Supervisão com contadores, fila e quadro),
// mais a grade por andar que o Hugo pediu.
//
// A tela NÃO decide estado: lê v_quadro_governanca e pinta. Toda escrita
// passa pelas portas de 0008 (iniciar_limpeza, atualizar_ficha,
// terminar_limpeza, cancelar_limpeza, inspecionar_limpeza) — se o banco
// recusar, a mensagem dele é a que aparece.
//
// Quem vê o quê: governanca_contexto() (0009) diz se eu inspeciono (tela de
// supervisão) ou se sigo escala (só meus andares; sem andar hoje, aviso).
// =====================================================================
import { el, icone, abrirModal, fecharModal, modalAberto, aviso, confirmar, enquantoAtualiza } from '../ui.js';
import {
  ROTULO_ESTADO, TIPOS, tipoParaIniciar, avisoAguardaCheckout, agruparPorAndar, listaDaCamareira, vagosDaCamareira,
  contadores, resumoDeTipos, filaDeInspecao, quadroDoDia, situacaoDoQuarto,
  progressoDaFicha, gruposDaFicha, itensDaFicha, marcarItem, itemFeito, hora, duracao, dataPorExtenso,
  avisoDeEscala, minhaEscalaPorExtenso, ordenarPorNecessidade, avisoChegada,
  grupoDaFicha, itemSolicitado, solicitacoesDaFicha,
} from '../lib/governanca.js';

const INTERVALO_QUADRO_MS = 30000;

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const eu = contexto.usuario;
  const nivel = (contexto.modulos.find(m => m.chave === 'governanca') || {}).nivel;
  const podeEditar = nivel === 'editar';

  let ctx = null;        // governanca_contexto()
  let quadro = [];       // v_quadro_governanca
  let itens = [];        // checklist_itens
  let vivo = true;
  let timerQuadro = null, timerCrono = null;
  let fichaAberta = null;  // {unidade_id} enquanto a ficha está na tela

  // ── carga ──────────────────────────────────────────────────────────
  async function carregarTudo() {
    const [c, q, i] = await Promise.all([
      auth.rpc('governanca_contexto', {}),
      auth.ler('v_quadro_governanca', 'select=*&order=andar.asc.nullslast,numero.asc'),
      auth.ler('checklist_itens', 'select=*&ativo=is.true&order=ordem.asc,rotulo.asc'),
    ]);
    // O corte por módulo contratado entra AQUI, na entrada, e não na hora de
    // desenhar: o mesmo `itens` alimenta o progresso da ficha. Esconder o
    // bloco FRIGOBAR e seguir contando os dois itens dele daria uma ficha
    // 15/17 que a camareira nunca conseguiria fechar.
    ctx = c; quadro = q; itens = itensDaFicha(i, { modulos: contexto.modulos });
  }
  async function recarregarQuadro() {
    quadro = await auth.ler('v_quadro_governanca', 'select=*&order=andar.asc.nullslast,numero.asc');
  }
  // O mesmo contador dos outros módulos (ui.js): quem olha — pessoa ou prova
  // — sabe quando a tela terminou de se refazer DEPOIS de uma ação.
  // `atualizarEmSilencio` (o polling) NÃO passa por aqui de propósito: se
  // passasse, um ciclo de fundo faria a prova achar que a ação dela já tinha
  // aparecido na tela. Contador que anda sozinho não prova nada.
  function refazer() { return enquantoAtualiza(async () => { await recarregarQuadro(); desenhar(); }); }
  const quarto = unidadeId => quadro.find(q => q.unidade_id === unidadeId);

  // ── desenho ────────────────────────────────────────────────────────
  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = ctx.inspeciona ? 'supervisao' : 'tarefas';
    if (ctx.inspeciona) desenharSupervisao(); else desenharTarefas();
  }

  function pilulaTipo(tipo, texto) {
    const t = TIPOS[tipo]; if (!t) return null;
    return el('span', { class: 'pill ' + t.classe, dataset: { tipo } }, icone(t.icone), texto || t.rotulo.toUpperCase());
  }
  function badgeEstado(q, extra) {
    return el('span', { class: 'estado est-' + q.estado, dataset: { estado: q.estado } },
      ROTULO_ESTADO[q.estado].toUpperCase(), extra ? ' · ' + extra : '');
  }
  function linhasDeMovimento(q) {
    const l = [];
    if (q.sai_hoje)   l.push(el('span', { style: { color: '#dc2626' } }, 'Saída hoje'));
    if (q.chega_hoje) l.push(el('span', { style: { color: '#059669' } }, 'Entrada hoje', avisoChegada(q) ? el('span', { style: { color: q.chega_cedo ? '#b45309' : '#6b7280', fontWeight: q.chega_cedo ? '700' : '400' }, dataset: { chegada: q.chegada_prevista } }, ' · ' + avisoChegada(q)) : null));
    if (q.ocupado && !q.sai_hoje && !q.chega_hoje) l.push(el('span', { style: { color: '#2563eb' } }, `Ocupado · ${q.pessoas} ${q.pessoas === 1 ? 'pessoa' : 'pessoas'}`));
    return l.length ? el('div', { class: 'linhas' }, l) : null;
  }
  function barraProgresso(q) {
    const total = q.checklist_total || 0, feitos = q.checklist_feitos || 0;
    const barra = el('div', { class: 'barra', 'aria-label': `ficha ${feitos}/${total}` });
    for (let i = 0; i < Math.min(total, 12); i++) barra.appendChild(el('i', { class: i < Math.round(feitos * Math.min(total, 12) / (total || 1)) ? 'ok' : '' }));
    return barra;
  }
  function crono(q) {
    if (q.estado !== 'em_limpeza') return null;
    return el('span', { class: 'crono', dataset: { inicio: q.iniciada_em } }, duracao(q.iniciada_em, agora()));
  }

  // ── TAREFAS (camareira / leitura) ──────────────────────────────────
  function desenharTarefas() {
    const cabeca = el('div', { class: 'gov-cabeca' },
      el('div', null,
        el('div', { style: { fontSize: '13px', color: '#6b7280' } }, dataPorExtenso(new Date(agora()))),
        ctx.segue_escala && (ctx.volante || ctx.andares_hoje.length)
          ? el('div', { style: { fontSize: '13px', color: '#1B3A7A', fontWeight: '600' }, id: 'meus-andares' }, 'Você hoje: ' + minhaEscalaPorExtenso(ctx))
          : null),
      el('div', { class: 'gov-resumo', id: 'resumo-tipos' }));
    cont.appendChild(cabeca);

    const avisoEscala = avisoDeEscala(ctx);
    if (avisoEscala) {
      cont.appendChild(el('div', { class: 'aviso-escala', id: 'aviso-escala' }, icone('ic-warning'), avisoEscala));
      // 0089: sem escala, mas com uma limpeza que ELA abriu e não terminou (a
      // supervisão remanejou no meio): a lista mostra essa limpeza
      if (!listaDaCamareira(quadro).length) return;
    }

    const lista = listaDaCamareira(quadro);
    const resumo = resumoDeTipos(lista);
    const r = cabeca.querySelector('#resumo-tipos');
    for (const [tipo, n] of Object.entries(resumo)) if (n) r.appendChild(pilulaTipo(tipo, `${n} ${TIPOS[tipo].rotulo.toLowerCase()}${n > 1 ? 's' : ''}`));

    if (!lista.length) {
      cont.appendChild(el('div', { class: 'vazio', id: 'lista-vazia', style: { padding: '40px 20px', textAlign: 'center' } },
        el('div', { style: { fontSize: '16px', fontWeight: '600', marginBottom: '6px' } }, 'Nenhum quarto para hoje'),
        el('div', { style: { fontSize: '13px' } }, ctx.segue_escala ? 'Não há saída, entrada nem quarto ocupado nos seus andares.' : 'Não há saída, entrada nem quarto ocupado.')));
    }
    // a volante vai aonde há mais vermelho: os andares vêm nessa ordem para ela
    const grupos = ctx.volante ? ordenarPorNecessidade(agruparPorAndar(lista)) : agruparPorAndar(lista);
    for (const grupo of grupos) {
      cont.appendChild(el('div', { class: 'andar-titulo' }, grupo.rotulo,
        ctx.volante ? el('span', { class: 'quem' }, `· ${grupo.quartos.filter(q => q.estado === 'a_limpar').length} a limpar`) : null));
      cont.appendChild(el('div', { class: 'quartos' }, grupo.quartos.map(cartao)));
    }

    const vagos = vagosDaCamareira(quadro);
    if (podeEditar && vagos.length) {
      const det = el('details', { id: 'vagos', style: { marginTop: '18px' } },
        el('summary', { style: { cursor: 'pointer', fontSize: '13px', color: '#6b7280', fontWeight: '600' } },
          `Quartos vagos sem movimento (${vagos.length}) — limpeza avulsa`),
        el('div', { class: 'grade-quartos', style: { marginTop: '8px' } },
          vagos.map(q => el('button', { type: 'button', class: 'grade-quarto est-sem_movimento', dataset: { quarto: q.numero },
            onclick: () => iniciar(q) }, q.numero))));
      cont.appendChild(det);
    }
  }

  function cartao(q) {
    const minha = q.camareira_id === eu.id;
    // é um <div role=button>, não um <button>: o cartão da volante carrega um
    // botão dentro ("Cama forrada"), e botão dentro de botão não é HTML válido.
    const c = el('div', { class: 'quarto est-' + q.estado, role: 'button', tabindex: '0', dataset: { quarto: q.numero, estado: q.estado },
      onclick: () => abrirQuarto(q),
      onkeydown: ev => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); abrirQuarto(q); } } },
      el('div', { class: 'numero' }, q.numero, el('small', null, `${q.checklist_feitos || 0}/${q.checklist_total || 0}`)),
      q.tipo_sugerido ? el('div', null, pilulaTipo(q.tipo_sugerido)) : (q.tipo ? el('div', null, pilulaTipo(q.tipo)) : null),
      linhasDeMovimento(q),
      q.estado === 'a_limpar' && q.inspecao_resultado === 'devolvida'
        ? el('div', { class: 'ficha-mov devolvida', style: { margin: '4px 0 0', padding: '6px 8px', fontSize: '11px' } },
            el('strong', null, 'Devolvido: '), q.inspecao_obs || '')
        : null,
      q.cama_forrada_por
        ? el('div', { class: 'linhas', style: { color: '#7c3aed' }, dataset: { camaForrada: '' } }, `Cama forrada · ${q.cama_forrada_por} às ${hora(q.cama_forrada_em)}`,
            // 0089 (decisão 7A): marcou no quarto errado na correria — desfaz no mesmo dia
            podeEditar && (q.cama_forrada_por === eu.nome || ctx.inspeciona)
              ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { acao: 'desfazer-cama' }, style: { alignSelf: 'flex-start', marginTop: '4px' },
                  onclick: ev => { ev.stopPropagation(); desfazerCama(q); } }, 'Desfazer')
              : null)
        : null,
      barraProgresso(q),
      el('div', { class: 'rodape' }, badgeEstado(q), crono(q),
        q.estado === 'em_limpeza' && !minha ? el('span', null, q.camareira_nome) : null,
        // a volante adianta: forra a cama sem abrir a limpeza
        ctx.volante && podeEditar && q.estado === 'a_limpar' && !q.cama_forrada_por
          ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { acao: 'cama-forrada' }, style: { marginLeft: 'auto' },
              onclick: ev => { ev.stopPropagation(); camaForrada(q); } }, 'Cama forrada')
          : null));
    if (!podeEditar && q.estado !== 'aguardando_inspecao' && q.estado !== 'liberado' && !q.limpeza_id) { c.classList.add('inerte'); c.setAttribute('aria-disabled', 'true'); c.tabIndex = -1; }
    return c;
  }

  // O clique num cartão: começar, continuar a ficha, ou só olhar.
  async function abrirQuarto(q) {
    if (q.estado === 'em_limpeza' && q.camareira_id !== eu.id) {
      aviso(`Quarto ${q.numero} está em limpeza por ${q.camareira_nome}.`);
      return;
    }
    if (q.estado === 'em_limpeza') { abrirFicha(q); return; }
    // 0086 (2B): antes do check-out, a saída não se limpa
    if (q.estado === 'aguarda_checkout') { aviso(avisoAguardaCheckout(q)); return; }
    if (q.estado === 'a_limpar' && podeEditar && !ctx.inspeciona) { await iniciar(q); return; }
    abrirFicha(q);   // aguardando / liberado / leitura: só olhar
  }

  async function desfazerCama(q) {
    try { await auth.rpc('desmarcar_cama_forrada', { p_unidade: q.unidade_id }); }
    catch (e) { aviso(e.message, 'erro'); }
    await refazer();
  }

  async function camaForrada(q) {
    try { await auth.rpc('marcar_cama_forrada', { p_unidade: q.unidade_id }); }
    catch (e) { aviso(e.message, 'erro'); }
    await refazer();
  }

  async function iniciar(q) {
    const ok = await confirmar({
      titulo: `Quarto ${q.numero}`,
      texto: `Está iniciando a limpeza deste quarto? O tempo será registrado em nome de ${eu.nome}.`,
      sim: 'Sim, começar', nao: 'Não',
    });
    if (!ok) return;
    try {
      await auth.rpc('iniciar_limpeza', { p_unidade: q.unidade_id, p_tipo: tipoParaIniciar(q) });
    } catch (e) {
      // 23505: alguém (ou um toque duplo) abriu antes — recarrega e mostra o que há
      if (e.codigo !== '23505') { aviso(e.message, 'erro'); return; }
    }
    await refazer();
    const novo = quarto(q.unidade_id);
    if (novo && novo.camareira_id === eu.id && novo.estado === 'em_limpeza') abrirFicha(novo);
  }

  // ── A FICHA ────────────────────────────────────────────────────────
  // Uma modal por quarto. Enquanto está em limpeza pela própria pessoa, os
  // itens gravam ao marcar (atualizar_ficha). Depois, é leitura — e, para a
  // supervisão, o lugar de devolver ou liberar.
  async function abrirFicha(q0) {
    fichaAberta = { unidade_id: q0.unidade_id };
    let q = q0;
    const minhaAberta = () => q.estado === 'em_limpeza' && q.camareira_id === eu.id;
    let checklist = { ...(q.checklist || {}) };
    let obs = q.obs || '';
    let salvarTimer = null, salvando = Promise.resolve();

    // a devolução anterior de hoje (se houver): a camareira precisa saber o que refazer
    let devolucao = null;
    if (q.estado !== 'a_limpar' || q.inspecao_resultado !== 'devolvida') {
      try {
        const d = await auth.ler('limpezas', `select=inspecao_obs,inspecionada_por_nome,inspecionada_em&unidade_id=eq.${q.unidade_id}&data=eq.${dataISOLocal()}&inspecao_resultado=eq.devolvida&order=inspecionada_em.desc&limit=1`);
        if (d && d.length && (!q.inspecionada_em || d[0].inspecionada_em > q.iniciada_em)) devolucao = d[0];
        if (d && d.length && q.estado === 'em_limpeza') devolucao = d[0];
      } catch (_) { /* sem a devolução a ficha ainda serve */ }
    } else devolucao = { inspecao_obs: q.inspecao_obs, inspecionada_por_nome: q.inspecionada_por_nome };

    const raiz = el('div', { id: 'ficha', dataset: { quarto: q.numero } });
    abrirModal(raiz, { largura: '560px', aoFechar: () => { fichaAberta = null; } });

    function salvar() {
      if (!minhaAberta()) return;
      clearTimeout(salvarTimer);
      salvarTimer = setTimeout(() => {
        salvando = salvando.then(() => auth.rpc('atualizar_ficha', { p_limpeza: q.limpeza_id, p_checklist: checklist, p_obs: obs || null }))
          .then(() => { q.checklist = checklist; atualizarProgresso(); })
          .catch(e => aviso(e.message, 'erro'));
      }, 400);
    }
    // Grava AGORA o que o debounce ainda não mandou. Usada antes de qualquer
    // coisa que tire a ficha da tela (terminar, abrir a intercorrência).
    async function descarregar() {
      clearTimeout(salvarTimer);
      await salvando;
      if (!minhaAberta()) return;
      try {
        await auth.rpc('atualizar_ficha', { p_limpeza: q.limpeza_id, p_checklist: checklist, p_obs: obs || null });
        q.checklist = checklist;
      } catch (e) { aviso(e.message, 'erro'); }
    }
    async function terminar() {
      clearTimeout(salvarTimer);
      await salvando;
      try {
        if (minhaAberta()) await auth.rpc('atualizar_ficha', { p_limpeza: q.limpeza_id, p_checklist: checklist, p_obs: obs || null });
        await auth.rpc('terminar_limpeza', { p_limpeza: q.limpeza_id });
      } catch (e) { aviso(e.message, 'erro'); return; }
      fecharModal(); fichaAberta = null;
      await refazer();
      aviso(`Quarto ${q.numero} terminado. Aguardando inspeção.`);
    }
    async function cancelar() {
      const ok = await confirmar({ titulo: 'Cancelar esta limpeza?', texto: 'O tempo será descartado.', sim: 'Cancelar limpeza', nao: 'Voltar', perigoso: true });
      if (!ok) { return; }
      try { await auth.rpc('cancelar_limpeza', { p_limpeza: q.limpeza_id }); }
      catch (e) { aviso(e.message, 'erro'); return; }
      fecharModal(); fichaAberta = null;
      await refazer();
    }

    const prog = el('span', { id: 'ficha-progresso', style: { marginLeft: 'auto', fontSize: '12px', color: '#9ca3af' } });
    // A conta por bloco é do MdM (`amenDone`/`manutDone`, index.html 15908):
    // num bloco de sete amenidades, "4/7" na régua do bloco poupa a camareira
    // de conferir item por item.
    const contasPorGrupo = [];
    function atualizarProgresso() {
      const p = progressoDaFicha(checklist, itens);
      prog.textContent = `${p.feitos}/${p.total}`;
      raiz.querySelector('#ficha-itens-feitos').textContent = `${p.feitos}/${p.total} itens`;
      for (const g of contasPorGrupo) {
        const feitos = g.itens.filter(i => itemFeito(checklist[i.chave])).length;
        g.alvo.textContent = `${feitos}/${g.itens.length}`;
      }
    }

    raiz.appendChild(el('div', { class: 'ficha-cabeca' },
      el('h2', { class: 'modal-title' }, `Quarto ${q.numero} — Ficha`),
      q.estado === 'em_limpeza'
        ? el('span', { class: 'crono', dataset: { inicio: q.iniciada_em } }, duracao(q.iniciada_em, agora()))
        : (q.terminada_em ? el('span', { class: 'crono parado' }, duracao(q.iniciada_em, agora(), q.terminada_em)) : null),
      prog));
    raiz.appendChild(el('div', { style: { fontSize: '12px', color: '#6b7280', marginBottom: '12px' } },
      dataPorExtenso(new Date(agora())),
      q.tipo ? ' · ' : null, q.tipo ? pilulaTipo(q.tipo) : null,
      q.camareira_nome && q.camareira_id !== eu.id ? ` · ${q.camareira_nome}` : null));

    if (q.sai_hoje)   raiz.appendChild(el('div', { class: 'ficha-mov saida' }, el('strong', null, 'CHECK-OUT HOJE'), ' · o hóspede sai hoje'));
    if (q.chega_hoje) raiz.appendChild(el('div', { class: 'ficha-mov entrada' }, el('strong', null, 'CHECK-IN HOJE'), ' · chega hóspede hoje', avisoChegada(q) ? ` · ${avisoChegada(q)}` : ''));
    if (q.ocupado && !q.sai_hoje && !q.chega_hoje) raiz.appendChild(el('div', { class: 'ficha-mov ocupado' }, el('strong', null, 'OCUPADO'), ` · ${q.pessoas} ${q.pessoas === 1 ? 'pessoa' : 'pessoas'} · arrumação`));
    if (devolucao) raiz.appendChild(el('div', { class: 'ficha-mov devolvida', id: 'ficha-devolucao' },
      el('strong', null, 'DEVOLVIDO'), devolucao.inspecionada_por_nome ? ` por ${devolucao.inspecionada_por_nome}` : '', ': ', devolucao.inspecao_obs || ''));

    const editavel = minhaAberta();
    // Uma linha OK / Solicito, cópia do `manutRow` do MdM (index.html, 15878).
    // A pílula do dono é nossa: lá o chamado não tem dono, aqui tem, e quem
    // aperta precisa ver que a TV vai para a imobiliária e a lâmpada para a
    // manutenção do hotel. Ver docs/quem-faz-o-que-no-garvey.md.
    function linhaOkSolicito(item) {
      const valor = checklist[item.chave];
      const linha = el('div', { class: 'ficha-ok-solicito' + (itemFeito(valor) ? ' ok' : itemSolicitado(valor) ? ' solicito' : ''),
        dataset: { item: item.chave } });
      const botao = (classe, rotulo, ic, v) => el('button', {
        type: 'button', class: classe, disabled: !editavel, dataset: { valor: v },
        title: v === 'solicito' ? 'Abre um chamado quando você terminar a ficha' : 'Está em ordem',
        onclick: () => { checklist = marcarItem(checklist, item, v); redesenharItem(item, linha); atualizarProgresso(); salvar(); },
      }, icone(ic), rotulo);
      linha.appendChild(el('span', { class: 'rotulo' }, item.rotulo));
      linha.appendChild(el('span', { class: 'ficha-dono ' + (item.responsavel || 'hotel'),
        title: item.responsavel === 'imobiliaria' ? 'Conserto é da imobiliária do flat' : 'Conserto é do hotel' },
        item.responsavel === 'imobiliaria' ? 'imobiliária' : 'hotel'));
      linha.appendChild(botao('btn-ok', 'OK', 'ic-check', 'ok'));
      linha.appendChild(botao('btn-solicito', 'Solicito', 'ic-wrench', 'solicito'));
      return linha;
    }
    function redesenharItem(item, linha) {
      const valor = checklist[item.chave];
      linha.className = 'ficha-ok-solicito' + (itemFeito(valor) ? ' ok' : itemSolicitado(valor) ? ' solicito' : '');
      atualizarAvisoChamados();
    }

    // `itens` já vem cortado por módulo (veja o carregamento acima), então
    // aqui não se repete a regra — repeti-la em dois lugares é como elas
    // divergem.
    for (const g of gruposDaFicha(itens, { modulos: contexto.modulos })) {
      const meta = grupoDaFicha(g.grupo);
      const conta = el('span', { class: 'conta' });
      raiz.appendChild(el('div', { class: 'ficha-grupo', style: { '--cor': meta.cor }, dataset: { grupo: g.grupo } },
        icone(meta.icone), meta.rotulo, conta));
      const caixa = el('div', { class: 'ficha-itens' }, g.itens.map(item => {
        if (item.tipo === 'ok_solicito') return linhaOkSolicito(item);
        if (item.tipo === 'quantidade') {
          const inp = el('input', { type: 'number', min: '0', step: '1', inputmode: 'numeric', value: String(Number(checklist[item.chave]) || 0),
            disabled: !editavel, dataset: { item: item.chave },
            oninput: ev => { checklist = marcarItem(checklist, item, ev.target.value); atualizarProgresso(); salvar(); } });
          return el('div', { class: 'ficha-item quantidade' },
            el('label', null, el('span', null, item.rotulo)), el('span', { class: 'qtd' }, 'Qtd: ', inp));
        }
        const cb = el('input', { type: 'checkbox', checked: itemFeito(checklist[item.chave]), disabled: !editavel, dataset: { item: item.chave },
          onchange: ev => { checklist = marcarItem(checklist, item, ev.target.checked); atualizarProgresso(); salvar(); } });
        return el('label', { class: 'ficha-item' }, cb, el('span', null, item.rotulo));
      }));
      raiz.appendChild(caixa);
      contasPorGrupo.push({ grupo: g.grupo, itens: g.itens, alvo: conta });

      // FRIGOBAR: o bloco "Frigobar checado" fica (é conferência da camareira),
      // mas o registro de itens vendidos/repostos NÃO mora aqui. Em 17/09 o
      // botão entrou desabilitado "para quando o módulo viesse"; em 22/09 o
      // Hugo decidiu (decisão 3 da 0056): "camareira do hotel não tem gestão
      // sobre frigobar". O registro é do DONO da unidade, na tela "Frigobar
      // do Dia" do módulo Loja e Frigobar (src/modulos/frigobar.js).
    }
    raiz.appendChild(el('div', { class: 'ficha-progresso', id: 'ficha-itens-feitos' }));

    // O que vai virar chamado quando ela apertar "Quarto terminado". Aparece
    // ANTES, porque o chamado só nasce ao terminar (terminar_limpeza, 0029) e
    // desmarcar tem de ser de graça.
    const avisoChamados = el('div', { class: 'ficha-aviso-chamados hidden', id: 'ficha-aviso-chamados' });
    raiz.appendChild(avisoChamados);
    function atualizarAvisoChamados() {
      const pedidos = solicitacoesDaFicha(checklist, itens);
      avisoChamados.classList.toggle('hidden', pedidos.length === 0);
      if (!pedidos.length) return;
      avisoChamados.replaceChildren(
        icone('ic-wrench'),
        el('strong', null, ` ${pedidos.length} ${pedidos.length === 1 ? 'pedido de conserto' : 'pedidos de conserto'}`),
        ` — abre${pedidos.length === 1 ? '' : 'm'} quando você terminar o quarto: `,
        pedidos.map(i => i.rotulo).join(' · '));
    }

    const ta = el('textarea', { class: 'input', id: 'ficha-obs', rows: '2', placeholder: 'Observações (faltou algo, avaria…)', disabled: !editavel,
      oninput: ev => { obs = ev.target.value; salvar(); } }, obs);
    raiz.appendChild(el('div', { style: { marginTop: '12px' } }, el('label', { class: 'label', for: 'ficha-obs' }, 'Observações'), ta));

    // Intercorrência: foto + descrição. Botão copiado do MdM (index.html,
    // 16048). Diferente do "Solicito", ela não depende de haver um item na
    // ficha — é para o que ninguém previu (vazamento, vidro trincado).
    //
    // Só existe UMA modal por vez (ui.js), então a intercorrência tapa a
    // ficha. Antes de sair, grava o que estiver pendente no debounce; ao
    // voltar, a ficha reabre no mesmo quarto — a camareira não pode perder o
    // que marcou porque foi reportar um vazamento.
    if (editavel) {
      raiz.appendChild(el('button', { type: 'button', class: 'ficha-intercorrencia', id: 'btn-intercorrencia',
        onclick: async () => {
          await descarregar();
          abrirIntercorrencia(q, () => abrirFicha({ ...q, checklist, obs }));
        } }, icone('ic-warning'), 'Reportar intercorrência (foto + descrição)'));
    }

    const rodape = el('div', { class: 'ficha-rodape' });
    if (editavel) {
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-verde', id: 'btn-terminar', onclick: terminar }, icone('ic-check'), 'Terminar limpeza'));
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-contorno-vermelho', id: 'btn-cancelar-limpeza', onclick: cancelar }, 'Cancelar'));
    } else if (ctx.inspeciona && q.estado === 'aguardando_inspecao') {
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-verde', id: 'btn-liberar', onclick: () => liberar(q) }, icone('ic-check'), 'Liberar quarto'));
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-contorno-vermelho', id: 'btn-devolver', onclick: () => devolver(q) }, 'Devolver'));
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-secondary', onclick: fecharModal }, 'Fechar'));
    } else if (ctx.inspeciona && q.estado === 'em_limpeza') {
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-contorno-vermelho', id: 'btn-cancelar-limpeza', onclick: async () => {
        const ok = await confirmar({ titulo: `Cancelar a limpeza do ${q.numero}?`, texto: `Aberta por ${q.camareira_nome}. O tempo será descartado.`, sim: 'Cancelar limpeza', nao: 'Voltar', perigoso: true });
        if (!ok) return;
        try { await auth.rpc('cancelar_limpeza', { p_limpeza: q.limpeza_id }); } catch (e) { aviso(e.message, 'erro'); return; }
        fecharModal(); fichaAberta = null; await refazer();
      } }, 'Cancelar limpeza'));
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-secondary', onclick: fecharModal }, 'Fechar'));
    } else {
      rodape.appendChild(el('button', { type: 'button', class: 'btn btn-secondary', onclick: fecharModal }, 'Fechar'));
    }
    raiz.appendChild(rodape);
    atualizarProgresso();
    atualizarAvisoChamados();
  }

  // ── INTERCORRÊNCIA ─────────────────────────────────────────────────
  // Uma modal em cima da ficha. A foto sobe para um bucket PRIVADO
  // (`intercorrencias`, 0029) e o banco guarda o CAMINHO, nunca uma URL
  // pública — quem for ver pede uma URL assinada de vida curta.
  //
  // Decisão do Hugo, 17/09: "intercorrência não tira fotos de pessoas, apenas
  // objetos quebrados, danificados, sujos ou situações". O bucket é privado
  // mesmo assim: uma câmera dentro de um quarto acaba pegando a mala de
  // alguém no canto do enquadramento, e link público vaza sozinho.
  function abrirIntercorrencia(q, aoVoltar) {
    // A câmera é desligada pelo `aoFechar` da modal, lá embaixo — este
    // caminho não precisa lembrar disso, e é justamente por isso que ele
    // mora lá e não aqui.
    const voltar = () => { fecharModal(); if (aoVoltar) aoVoltar(); };
    let arquivo = null;
    const raiz = el('div', { id: 'intercorrencia', dataset: { quarto: q.numero } });
    const previa = el('img', { class: 'ficha-foto-previa', id: 'intercorrencia-previa', alt: 'Foto do problema' });

    // ── A FOTO ───────────────────────────────────────────────────────
    // Três estados, um de cada vez: os dois botões, o visor ao vivo, a
    // prévia. Cópia de `modalNovaIntercorrencia` + `_icAbrirCamera` +
    // `_icCapturarCamera` (MdM, index.html 23043 e 23111).
    //
    // Decisão do Hugo, 17/09: visor DENTRO da página, com queda. Tenta
    // `getUserMedia` com a câmera traseira; se o navegador não tiver a API,
    // se a permissão for negada, ou se der qualquer outro erro, cai sozinho
    // para a câmera do sistema (`capture="environment"`), que no celular
    // abre o visor nativo. Assim há visor ao vivo nos dois caminhos, e nunca
    // se fica sem foto por causa de uma permissão.
    //
    // Onde divergimos dele de propósito: lá a falha vira `alert()` e a
    // pessoa tem de escolher a galeria na mão. Aqui a queda é automática e o
    // motivo aparece como aviso, porque quem está com o quarto pela metade
    // não vai ler uma caixa de diálogo e decidir sobre permissão de câmera.
    let fluxo = null;   // MediaStream enquanto o visor está aberto

    const video = el('video', { id: 'intercorrencia-video', autoplay: true, playsinline: true, muted: true });
    const capturar = el('button', { type: 'button', class: 'capturar', id: 'btn-capturar-foto' }, icone('ic-check'), 'Capturar');
    const visor = el('div', { class: 'ic-camera hidden', id: 'intercorrencia-visor' }, video,
      el('div', { class: 'ic-camera-botoes' },
        el('button', { type: 'button', class: 'desistir', onclick: () => { pararCamera(); mostrar('acoes'); } }, 'Cancelar'),
        capturar));

    // O seletor de arquivo. `comCamera` põe `capture="environment"`: é o que
    // abre a câmera do sistema no celular. Um <input> novo a cada vez porque
    // reusar um já preenchido não dispara `change` ao escolher o mesmo arquivo.
    const escolher = (comCamera) => {
      const velho = raiz.querySelector('#intercorrencia-foto');
      if (velho) velho.remove();
      const inp = el('input', { type: 'file', accept: 'image/*', id: 'intercorrencia-foto',
        style: { display: 'none' },
        onchange: ev => {
          const f = ev.target.files && ev.target.files[0] ? ev.target.files[0] : null;
          if (f) usarFoto(f);
        } });
      if (comCamera) inp.setAttribute('capture', 'environment');
      raiz.appendChild(inp);
      inp.click();
    };

    function usarFoto(f) {
      arquivo = f;
      previa.src = URL.createObjectURL(f);
      mostrar('previa');
    }
    function pararCamera() {
      try { if (fluxo) fluxo.getTracks().forEach(t => t.stop()); } catch (_) { /* já parado */ }
      fluxo = null;
      try { video.srcObject = null; } catch (_) { /* idem */ }
    }
    async function abrirCamera() {
      const md = navigator.mediaDevices;
      if (!md || !md.getUserMedia) { escolher(true); return; }
      try {
        fluxo = await md.getUserMedia({ video: { facingMode: { ideal: 'environment' }, width: { ideal: 1920 }, height: { ideal: 1080 } }, audio: false });
      } catch (e) {
        // Permissão negada, câmera ocupada, página sem HTTPS: cai para a
        // câmera do sistema em vez de deixar a pessoa sem saída.
        aviso('Não consegui abrir a câmera aqui dentro; vou usar a câmera do aparelho.', 'erro');
        escolher(true);
        return;
      }
      video.srcObject = fluxo;
      try { await video.play(); } catch (_) { /* alguns navegadores já tocam sozinhos */ }
      mostrar('visor');
    }
    capturar.onclick = () => {
      const l = video.videoWidth || 1280, a = video.videoHeight || 720;
      const tela = document.createElement('canvas');
      tela.width = l; tela.height = a;
      tela.getContext('2d').drawImage(video, 0, 0, l, a);
      pararCamera();
      tela.toBlob(b => {
        if (!b) { aviso('Não consegui capturar a imagem. Tente pela galeria.', 'erro'); mostrar('acoes'); return; }
        // `enviarFoto` lê `.type` e `.size`; um Blob tem os dois. O nome não
        // vai para lugar nenhum — o caminho no bucket é sorteado.
        usarFoto(b);
      }, 'image/jpeg', 0.85);
    };

    const acoes = el('div', { class: 'ic-foto-acoes', id: 'intercorrencia-acoes' },
      el('button', { type: 'button', class: 'camera', onclick: abrirCamera }, icone('ic-camera'), 'Tirar foto agora'),
      el('button', { type: 'button', class: 'galeria', onclick: () => escolher(false) }, icone('ic-import'), 'Galeria / arquivo'));
    const trocar = el('button', { type: 'button', class: 'ic-trocar', id: 'btn-trocar-foto',
      onclick: () => { arquivo = null; previa.removeAttribute('src'); mostrar('acoes'); } },
      icone('ic-refresh'), 'Trocar ou tirar outra foto');
    const blocoPrevia = el('div', { class: 'ic-previa hidden', id: 'intercorrencia-previa-bloco' }, previa, trocar);
    function mostrar(qual) {
      acoes.classList.toggle('hidden', qual !== 'acoes');
      visor.classList.toggle('hidden', qual !== 'visor');
      blocoPrevia.classList.toggle('hidden', qual !== 'previa');
    }

    const desc = el('textarea', { class: 'input', id: 'intercorrencia-descricao', rows: '3',
      placeholder: 'O que aconteceu? Ex.: vazamento embaixo da pia, vidro do box trincado…' });
    const botao = el('button', { type: 'button', class: 'btn btn-contorno-vermelho', id: 'btn-salvar-intercorrencia' },
      icone('ic-warning'), 'Registrar intercorrência');

    botao.onclick = async () => {
      const texto = desc.value.trim();
      if (!texto) { aviso('Descreva o que aconteceu.', 'erro'); desc.focus(); return; }
      botao.disabled = true;
      try {
        let caminho = null;
        if (arquivo) caminho = await auth.enviarFoto('intercorrencias', arquivo);
        await auth.rpc('abrir_intercorrencia', { p_unidade: q.unidade_id, p_descricao: texto, p_foto: caminho });
      } catch (e) { aviso(e.message, 'erro'); botao.disabled = false; return; }
      voltar();
      aviso(`Intercorrência registrada no ${q.numero}. A manutenção foi avisada.`);
    };

    raiz.appendChild(el('h2', { class: 'modal-title' }, `Quarto ${q.numero} — intercorrência`));
    raiz.appendChild(el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '12px' } },
      'Para o que não está na ficha: vazamento, vidro trincado, mancha que não sai. Fotografe o objeto ou a situação — não as pessoas.'));
    raiz.appendChild(el('div', null, el('label', { class: 'label' }, 'Foto (opcional)'), acoes, visor, blocoPrevia));
    raiz.appendChild(el('div', { style: { marginTop: '12px' } }, el('label', { class: 'label', for: 'intercorrencia-descricao' }, 'O que aconteceu'), desc));
    // A ordem e as larguras do MdM: Cancelar à esquerda com um terço, o
    // vermelho à direita com dois — o botão que registra é o maior.
    botao.style.flex = '2';
    raiz.appendChild(el('div', { class: 'ficha-rodape' },
      el('button', { type: 'button', class: 'btn btn-secondary', id: 'btn-voltar-intercorrencia', onclick: voltar }, 'Voltar'),
      botao));
    // `aoFechar` (ui.js) pega TODOS os caminhos de saída: botão, Esc, clique
    // fora. Sem isto, a luz da câmera ficaria acesa com a modal já fechada —
    // e é o tipo de coisa que a camareira lê como "o app está me filmando".
    abrirModal(raiz, { largura: '460px', aoFechar: pararCamera });
    desc.focus();
  }

  // ── SUPERVISÃO ─────────────────────────────────────────────────────
  function desenharSupervisao() {
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { style: { fontSize: '13px', color: '#6b7280' } }, 'O quarto só fica verde para a recepção depois que você inspeciona.'),
      el('div', { style: { fontSize: '12px', color: '#9ca3af' } }, `${dataPorExtenso(new Date(agora()))} · atualiza a cada 30 s`)));

    const c = contadores(quadro);
    cont.appendChild(el('div', { class: 'contadores', id: 'contadores' },
      ['a_limpar', 'em_limpeza', 'aguardando_inspecao', 'liberado'].map(e =>
        el('div', { class: 'contador est-' + e, dataset: { estado: e } }, el('div', { class: 'n' }, String(c[e])), el('div', { class: 't' }, ROTULO_ESTADO[e].toUpperCase())))));

    cont.appendChild(el('div', { class: 'secao-titulo' }, 'Fila de inspeção'));
    const fila = filaDeInspecao(quadro);
    if (!fila.length) cont.appendChild(el('p', { class: 'vazio', id: 'fila-vazia' }, 'Nenhum quarto aguardando inspeção.'));
    for (const q of fila) {
      cont.appendChild(el('div', { class: 'fila-item', dataset: { quarto: q.numero } },
        el('div', { class: 'info' },
          el('div', { class: 'n' }, `Quarto ${q.numero}`, ' ', q.tipo ? pilulaTipo(q.tipo) : null),
          el('div', { class: 's' }, `${q.camareira_nome} · terminou às ${hora(q.terminada_em)} · ${duracao(q.iniciada_em, agora(), q.terminada_em)}`),
          el('div', { class: 'espera' }, 'esperando há ', el('span', { class: 'espera-desde', dataset: { inicio: q.terminada_em } }, duracao(q.terminada_em, agora()))),
          el('div', { class: 's' }, `ficha ${q.checklist_feitos}/${q.checklist_total}`, q.obs ? ` · ${q.obs}` : '')),
        el('div', { class: 'acoes' },
          el('button', { type: 'button', class: 'btn btn-secondary btn-sm', onclick: () => abrirFicha(q) }, 'Ver ficha'),
          el('button', { type: 'button', class: 'btn btn-contorno-vermelho btn-sm', dataset: { acao: 'devolver' }, onclick: () => devolver(q) }, 'Devolver'),
          el('button', { type: 'button', class: 'btn btn-verde btn-sm', dataset: { acao: 'liberar' }, onclick: () => liberar(q) }, 'Liberar quarto'))));
    }

    cont.appendChild(el('div', { class: 'secao-titulo' }, 'Grade por andar'));
    const grade = el('div', { id: 'grade' });
    for (const g of agruparPorAndar(quadro)) {
      const escaladas = [...new Set(g.quartos.flatMap(q => q.escaladas || []))];
      grade.appendChild(el('div', { class: 'grade-andar', dataset: { andar: g.andar === null ? '' : String(g.andar) } },
        el('div', { class: 'andar-titulo' }, g.rotulo,
          el('span', { class: 'quem' }, escaladas.length ? '· ' + escaladas.join(', ') : '· ninguém escalado hoje')),
        el('div', { class: 'grade-quartos' }, g.quartos.map(q =>
          el('button', { type: 'button', class: 'grade-quarto est-' + q.estado, dataset: { quarto: q.numero, estado: q.estado },
            title: `${q.numero} — ${ROTULO_ESTADO[q.estado]}${q.tipo_sugerido ? ' · ' + TIPOS[q.tipo_sugerido].rotulo : ''}`,
            onclick: () => abrirFicha(q) }, q.numero)))));
    }
    cont.appendChild(grade);

    cont.appendChild(el('div', { class: 'secao-titulo' }, 'Quadro do dia'));
    const linhas = quadroDoDia(quadro);
    if (!linhas.length) cont.appendChild(el('p', { class: 'vazio' }, 'Nenhum movimento hoje.'));
    for (const q of linhas) {
      cont.appendChild(el('div', { class: 'quadro-linha est-' + q.estado, dataset: { quarto: q.numero } },
        el('span', { class: 'n' }, `Quarto ${q.numero}`), badgeEstado(q), q.tipo_sugerido ? pilulaTipo(q.tipo_sugerido) : null,
        el('span', { class: 'obs' }, situacaoDoQuarto(q, agora()))));
    }
  }

  async function liberar(q) {
    const ok = await confirmar({ titulo: `Liberar o quarto ${q.numero}?`, texto: `Limpo por ${q.camareira_nome}. Fica verde para a recepção.`, sim: 'Liberar', nao: 'Voltar' });
    if (!ok) return;
    try { await auth.rpc('inspecionar_limpeza', { p_limpeza: q.limpeza_id, p_resultado: 'aprovada', p_obs: null }); }
    catch (e) { aviso(e.message, 'erro'); return; }
    fecharModal(); fichaAberta = null;
    await refazer();
    aviso(`Quarto ${q.numero} liberado.`);
  }

  function devolver(q) {
    const ta = el('textarea', { class: 'input', id: 'motivo', rows: '3', placeholder: 'O que precisa ser refeito?' });
    const erro = el('div', { class: 'alert alert-error hidden', id: 'devolver-erro' });
    abrirModal([
      el('h2', { class: 'modal-title', style: { marginBottom: '6px' } }, `Devolver o quarto ${q.numero}`),
      el('div', { style: { fontSize: '13px', color: '#6b7280', marginBottom: '12px' } }, `Volta para ${q.camareira_nome} como "a limpar", com o motivo à vista.`),
      el('label', { class: 'label', for: 'motivo' }, 'Motivo (obrigatório)'), ta, erro,
      el('div', { style: { display: 'flex', gap: '10px', marginTop: '14px' } },
        el('button', { type: 'button', class: 'btn btn-vermelho', style: { flex: '1' }, id: 'btn-confirmar-devolver', onclick: async () => {
          erro.classList.add('hidden');
          try { await auth.rpc('inspecionar_limpeza', { p_limpeza: q.limpeza_id, p_resultado: 'devolvida', p_obs: ta.value }); }
          catch (e) { erro.textContent = e.message; erro.classList.remove('hidden'); return; }
          fecharModal(); fichaAberta = null;
          await refazer();
          aviso(`Quarto ${q.numero} devolvido.`);
        } }, 'Devolver'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Voltar')),
    ], { fixo: true });
    ta.focus();
  }

  // ── relógios ───────────────────────────────────────────────────────
  function tique() {
    const t = agora();
    for (const e of document.querySelectorAll('.crono[data-inicio], .espera-desde[data-inicio]')) e.textContent = duracao(e.dataset.inicio, t);
  }
  async function atualizarEmSilencio() {
    if (!vivo) return;
    try { await recarregarQuadro(); } catch (_) { return; }
    if (!vivo) return;
    if (!modalAberto()) desenhar();
  }

  function dataISOLocal() {
    const d = new Date(agora()); const p = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
  }

  // ── liga ───────────────────────────────────────────────────────────
  cont.replaceChildren(el('p', { class: 'vazio' }, 'Carregando o quadro…'));
  carregarTudo().then(() => {
    if (!vivo) return;
    desenhar();
    timerCrono = setInterval(tique, 1000);
    timerQuadro = setInterval(atualizarEmSilencio, INTERVALO_QUADRO_MS);
  }).catch(e => {
    if (!vivo) return;
    cont.replaceChildren(el('div', { class: 'alert alert-error' }, 'Não foi possível carregar o quadro: ', e.message));
  });

  return function desmontar() {
    vivo = false;
    clearInterval(timerCrono); clearInterval(timerQuadro);
    fecharModal();
  };
}
